import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2 } from 'lucide-react';

interface SaveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTitle: string;
  existingId: string | null;
  onSave: (title: string, category: string, asNew: boolean, isOfficial: boolean) => Promise<void>;
}

export function SaveDialog({ open, onOpenChange, defaultTitle, existingId, onSave }: SaveDialogProps) {
  const [title, setTitle] = useState(defaultTitle);
  const [category, setCategory] = useState('workflow');
  const [isOfficial, setIsOfficial] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync default title when dialog opens
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setTitle(defaultTitle);
    }
    onOpenChange(next);
  };

  const handleSave = async (asNew: boolean) => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave(title.trim(), category, asNew, isOfficial);
      onOpenChange(false);
    } catch {
      // error toast is handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Spara workflow</DialogTitle>
          <DialogDescription>
            Spara ditt arbetsflöde till databasen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="doc-title">Titel</Label>
            <Input
              id="doc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Namn på workflow..."
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="doc-category">Kategori</Label>
            <Input
              id="doc-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="workflow"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="doc-official"
              checked={isOfficial}
              onCheckedChange={(checked) => setIsOfficial(checked === true)}
            />
            <Label htmlFor="doc-official" className="cursor-pointer text-sm font-normal">
              Officiellt dokument
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {existingId && (
            <Button
              variant="outline"
              onClick={() => handleSave(true)}
              disabled={saving || !title.trim()}
            >
              Spara som ny
            </Button>
          )}
          <Button
            onClick={() => handleSave(false)}
            disabled={saving || !title.trim()}
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {existingId ? 'Uppdatera' : 'Spara'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
